import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '../Typography';
import Button from '../Button/Button';
import Drawer from './Drawer';
import Tag from '../Tag';
import { theme } from '../../theme';
import { statusTagVariants } from '../../utils/statusColors';
import { useAttendeeStateTranslations } from '../../constants/attendeeStates';
import { TICKET_FREE, TICKET_PAID } from '../../constants/eventTypes';
import CloseIcon from '../Icons/CloseIcon';
import HelmetIcon from '../Icons/HelmetIcon';
import CheckCircleIcon from '../Icons/CheckCircleIcon';
import CancelCircleIcon from '../Icons/CancelCircleIcon';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import ChevronRightIcon from '../Icons/ChevronRightIcon';
import ArrowRightUpIcon from '../Icons/ArrowRightUpIcon';
import RejectRegistrationModal from '../Modal/RejectRegistrationModal';

const AttendeeDetailDrawer = ({
  isOpen,
  onClose,
  attendee,
  onApprove,
  onReject,
  onRefundAction,
  onCancelByOrganizerClick,
  onMarkRefunded,
  anchor = 'right',
  width = '500px',
  isLoading = false
}) => {
  const { t } = useTranslation('manageEventAttendees');
  const { getStateLabel: getAttendeeStateLabel } = useAttendeeStateTranslations();
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  
  if (!attendee) return null;

  const handleRejectWithMessage = async (attendeeId, message) => {
    try {
      await onReject(attendeeId, message);
      // Solo cerrar el modal y drawer si la operación fue exitosa
      setIsRejectModalOpen(false);
      onClose();
    } catch (error) {
      // Si hay error, solo cerrar el modal interno pero mantener el drawer abierto
      setIsRejectModalOpen(false);
      console.error('Error al rechazar inscripción:', error);
    }
  };



  return (
    <>
      <Drawer anchor={anchor} open={isOpen} onClose={onClose} width={width}>
        <DrawerContainer>
          <DrawerHeader>
            <Typography as="h2" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold">
              {t('attendeeDetailDrawer.title')}
            </Typography>
            <Button
              $iconOnly={true}
              onClick={onClose}
              $variant="ghost"
              size="l"
              sizeTablet="l"
              sizeMobile="m"
            >
              <CloseIcon fill={theme.colors.defaultMain} width={20} height={20} />
            </Button>
          </DrawerHeader>

          <DrawerBody>
            {/* Información del usuario */}
            <Section>
              <UserLink to={`/user/${attendee.userId._id}`}>
                <UserInfo>
                  <UserAvatar>
                    {attendee.userId.userAvatar ? (
                      <Avatar src={attendee.userId.userAvatar} alt={`${attendee.userId.name} avatar`} />
                    ) : (
                      <AvatarPlaceholder>
                        <HelmetIcon width={32} height={32} fill={theme.colors.defaultStrong} />
                      </AvatarPlaceholder>
                    )}
                  </UserAvatar>
                  <UserDetails>
                    <Typography as="p" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
                      {attendee.userId.name} {attendee.userId.lastName}
                    </Typography>
                    <Typography as="p" $variant="body-3-medium" color={theme.colors.defaultWeak}>
                      {attendee.userId.email}
                    </Typography>
                  </UserDetails>
                </UserInfo>
                <ChevronContainer>
                  <ChevronRightIcon fill={theme.colors.defaultWeak} width={20} height={20} />
                </ChevronContainer>
              </UserLink>
            </Section>

            {/* Información de la inscripción */}
            <Section>
              <SectionWrapper>
                <SectionHeader>
                  <Typography as="h3" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                    {t('attendeeDetailDrawer.sections.registrationInfo')}
                  </Typography>
                </SectionHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                        {t('attendeeDetailDrawer.labels.ticket')}
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Typography as="span" $variant="body-3-regular">
                        {attendee.ticketId.name}
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                        {t('attendeeDetailDrawer.labels.price')}
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Typography as="span" $variant="body-3-regular">
                        {attendee.ticketId.type === TICKET_FREE ? t('attendeeDetailDrawer.common.free') : `${attendee.ticketId.price}€`}
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                        {t('attendeeDetailDrawer.labels.date')}
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Typography as="span" $variant="body-3-regular">
                        {format(new Date(attendee.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                        {t('attendeeDetailDrawer.labels.status')}
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Tag $variant={statusTagVariants[attendee.status]}>
                        {getAttendeeStateLabel(attendee.status)}
                      </Tag>
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
              </SectionWrapper>
            </Section>

            {/* Información del vehículo */}
            {attendee.vehicleId && (
              <Section>
                <SectionWrapper>
                  <SectionHeader>
                    <Typography as="h3" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                      {t('attendeeDetailDrawer.sections.vehicleInfo')}
                    </Typography>
                  </SectionHeader>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>
                        <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {t('attendeeDetailDrawer.labels.brand')}
                        </Typography>
                      </InfoLabel>
                      <InfoValue>
                        <Typography as="span" $variant="body-3-regular">
                          {attendee.vehicleId.brand}
                        </Typography>
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>
                        <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {t('attendeeDetailDrawer.labels.model')}
                        </Typography>
                      </InfoLabel>
                      <InfoValue>
                        <Typography as="span" $variant="body-3-regular">
                          {attendee.vehicleId.model}
                        </Typography>
                      </InfoValue>
                    </InfoItem>
                    {attendee.vehicleId.nickname && (
                      <InfoItem>
                        <InfoLabel>
                          <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                            {t('attendeeDetailDrawer.labels.nickname')}
                          </Typography>
                        </InfoLabel>
                        <InfoValue>
                          <Typography as="span" $variant="body-3-regular">
                            {attendee.vehicleId.nickname}
                          </Typography>
                        </InfoValue>
                      </InfoItem>
                    )}
                  </InfoGrid>
                </SectionWrapper>
              </Section>
            )}

            {/* Conversación de cancelación */}
            {((attendee.status === 'cancellation_pending' || attendee.status === 'attending' || attendee.status === 'refunded' || attendee.status === 'cancelled_by_organizer' || attendee.status === 'cancelled_by_user' || attendee.status === 'refund_accepted' || attendee.status === 'rejected_by_organizer' || attendee.cancellationDenied) && (attendee.userMessage || attendee.organizerMessage)) ? (
              <Section>
                <SectionWrapper>
                  <SectionHeader>
                    <Typography as="h3" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                      {attendee.userMessage && attendee.organizerMessage ? t('attendeeDetailDrawer.sections.conversation') :
                        attendee.userMessage ? t('attendeeDetailDrawer.sections.attendeeMessage') : t('attendeeDetailDrawer.sections.organizerMessage')}
                    </Typography>
                  </SectionHeader>
                  <ConversationContainer>
                    {/* Mensaje del asistente */}
                    {attendee.userMessage && (
                      <MessageContainer $isUser={true}>
                        <MessageWrapper>
                          <MessageHeader>
                            <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {attendee.userId.name} {attendee.userId.lastName}
                            </Typography>
                            <Typography as="span" $variant="body-3-regular" color={theme.colors.defaultWeak}>
                              {attendee.status === 'cancelled_by_user' ?
                                (attendee.ticketId?.type === TICKET_PAID ? t('attendeeDetailDrawer.messageTypes.cancellationWithRefund') : t('attendeeDetailDrawer.messageTypes.cancellationRegistration')) :
                                (attendee.ticketId?.type === TICKET_PAID ? t('attendeeDetailDrawer.messageTypes.refundRequest') : t('attendeeDetailDrawer.messageTypes.cancellationRequest'))
                              }
                            </Typography>
                          </MessageHeader>
                          <MessageContent>
                            <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultStrong}>
                              {attendee.userMessage}
                            </Typography>
                          </MessageContent>
                        </MessageWrapper>
                      </MessageContainer>
                    )}

                    {/* Mensaje del organizador */}
                    {attendee.organizerMessage && (
                      <MessageContainer $isUser={false}>
                        <MessageWrapper>
                          <MessageHeader>
                            <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {t('attendeeDetailDrawer.common.organizer')}
                            </Typography>
                            <Typography as="span" $variant="body-3-regular" color={theme.colors.defaultWeak}>
                              {attendee.status === 'refunded' ? t('attendeeDetailDrawer.organizerMessageTypes.refundProcessed') :
                                attendee.status === 'refund_accepted' ? t('attendeeDetailDrawer.organizerMessageTypes.refundAccepted') :
                                attendee.status === 'rejected_by_organizer' ? t('attendeeDetailDrawer.organizerMessageTypes.registrationRejected') :
                                  attendee.cancellationDenied ? t('attendeeDetailDrawer.organizerMessageTypes.cancellationDenied') : t('attendeeDetailDrawer.organizerMessageTypes.response')}
                            </Typography>
                          </MessageHeader>
                          <MessageContent>
                            <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultStrong}>
                              {attendee.organizerMessage}
                            </Typography>
                          </MessageContent>
                        </MessageWrapper>
                      </MessageContainer>
                    )}
                  </ConversationContainer>

                </SectionWrapper>
              </Section>
            ) : null}

          </DrawerBody>

          {/* Acciones del organizador */}
          {(attendee.status === 'confirmation_pending' ||
            attendee.status === 'cancellation_pending' ||
            attendee.status === 'attending' ||
            (attendee.status === 'cancelled_by_organizer' && attendee.ticketId && attendee.ticketId.type === TICKET_PAID)) && (
              <DrawerFooter>
                {/* <SectionHeader>
              <Typography as="h3" $variant="title-6-semibold">
                Acciones
              </Typography>
            </SectionHeader> */}
                <ActionsContainer>
                  {attendee.status === 'confirmation_pending' && (
                    <>
                      <Button
                        $variant="outlineSuccess"
                        size="l"
                        sizeTablet="l"
                        sizeMobile="m"
                        onClick={() => {
                          onApprove(attendee._id);
                          onClose();
                        }}
                      >
                        <CheckCircleIcon width={16} height={16} fill={theme.colors.successMain} />
                        {t('attendeeDetailDrawer.actions.approveRegistration')}
                      </Button>
                      <Button
                        $variant="outlineDanger"
                        size="l"
                        sizeTablet="l"
                        sizeMobile="m"
                        onClick={() => setIsRejectModalOpen(true)}
                      >
                        <CancelCircleIcon width={16} height={16} fill={theme.colors.errorMain} />
                        {t('attendeeDetailDrawer.actions.rejectRegistration')}
                      </Button>
                    </>
                  )}
                  {attendee.status === 'attending' && (
                    <>
                      {attendee.ticketId && attendee.ticketId.type === TICKET_PAID && (
                        <Button
                          $variant="outline"
                          size="l"
                          sizeTablet="l"
                          sizeMobile="m"
                          // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
                          onClick={() => {
                            window.open('https://dashboard.stripe.com/payments', '_blank');
                          }}
                        >
                          {/* <LinkIcon width={16} height={16} fill={theme.colors.defaultMain} /> */}
                          {t('attendeeDetailDrawer.actions.viewTransaction')}
                          <ArrowRightUpIcon width={16} height={16} fill={theme.colors.defaultMain} />
                        </Button>
                      )}
                      <Button
                        $variant="outlineDanger"
                        size="l"
                        sizeTablet="l"
                        sizeMobile="m"
                        // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
                        onClick={onCancelByOrganizerClick}
                      >
                        <CancelCircleIcon width={16} height={16} fill={theme.colors.errorMain} />
                        {t('attendeeDetailDrawer.actions.cancelRegistration')}
                      </Button>
                    </>
                  )}
                  {attendee.status === 'cancellation_pending' && (
                    <>
                      {attendee.ticketId && attendee.ticketId.type === TICKET_PAID ? (
                        // Tickets pagados - usar funciones de reembolso
                        <>
                          <Button
                            $variant=""
                            size="l"
                            sizeTablet="l"
                            sizeMobile="m"
                            // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
                            onClick={() => onRefundAction('accept')}
                          >
                            <CheckCircleIcon width={16} height={16} fill={theme.colors.successMain} />
                            {t('attendeeDetailDrawer.actions.acceptRefund')}
                          </Button>
                          <Button
                            $variant="outlineDanger"
                            size="l"
                            sizeTablet="l"
                            sizeMobile="m"
                            // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
                            onClick={() => onRefundAction('deny')}
                          >
                            <CancelCircleIcon width={16} height={16} fill={theme.colors.errorMain} />
                            {t('attendeeDetailDrawer.actions.denyRefund')}
                          </Button>
                        </>
                      ) : (
                        // Tickets gratis - usar funciones de cancelación
                        <>
                          <Button
                            $variant=""
                            size="l"
                            sizeTablet="l"
                            sizeMobile="m"
                            onClick={() => {
                              onApprove(attendee._id);
                              onClose();
                            }}
                          >
                            <CheckCircleIcon width={16} height={16} fill={theme.colors.successMain} />
                            {t('attendeeDetailDrawer.actions.acceptCancellation')}
                          </Button>
                          <Button
                            $variant="outlineDanger"
                            size="l"
                            sizeTablet="l"
                            sizeMobile="m"
                            onClick={() => {
                              onRefundAction('deny');
                              onClose();
                            }}
                          >
                            <CancelCircleIcon width={16} height={16} fill={theme.colors.errorMain} />
                            {t('attendeeDetailDrawer.actions.denyCancellation')}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  {attendee.status === 'cancelled_by_organizer' && attendee.ticketId && attendee.ticketId.type === TICKET_PAID && (
                    <>
                      <Button
                        $variant=""
                        size="l"
                        sizeTablet="l"
                        sizeMobile="m"
                        onClick={() => {
                          window.open('https://dashboard.stripe.com/payments', '_blank');
                        }}
                      >
                        {/* <LinkIcon width={16} height={16} fill={theme.colors.defaultMain} /> */}
                        {t('attendeeDetailDrawer.actions.processRefund')}
                        <ArrowRightUpIcon width={16} height={16} fill={theme.colors.inverseMain} />
                      </Button>
                      <Button
                        $variant="outline"
                        size="l"
                        sizeTablet="l"
                        sizeMobile="m"
                        onClick={() => {
                          onMarkRefunded(attendee._id);
                          onClose();
                        }}
                      >
                        <CheckCircleIcon width={16} height={16} fill={theme.colors.successMain} />
                        {t('attendeeDetailDrawer.actions.markAsRefunded')}
                      </Button>
                    </>
                  )}
                </ActionsContainer>
              </DrawerFooter>
            )}
        </DrawerContainer>
      </Drawer>
      
      <RejectRegistrationModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        attendee={attendee}
        onConfirm={handleRejectWithMessage}
        isLoading={isLoading}
      />
    </>
  );
};

AttendeeDetailDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attendee: PropTypes.object,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onRefundAction: PropTypes.func,
  onCancelByOrganizerClick: PropTypes.func,
  onMarkRefunded: PropTypes.func,
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool
};

export default AttendeeDetailDrawer;

// Styled Components
const DrawerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  flex-shrink: 0;
  z-index: 1;
`;

const DrawerBody = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
`;

const DrawerFooter = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

// const SectionHeader = styled.div``;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${({ theme }) => theme.sizing.xs} 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const UserLink = styled(Link)`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: ${({ theme }) => theme.radius.xs};
  text-decoration: none;
  color: inherit;
  // border: 1px solid transparent;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  transition: all 0.3s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
    border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  cursor: pointer;
`;

const UserAvatar = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 48px;
    height: 48px;
  }
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  object-fit: cover;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 48px;
    height: 48px;
  }
`;

const AvatarPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultMain};
  background-color: ${({ theme }) => theme.fill.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 48px;
    height: 48px;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  // padding: ${({ theme }) => theme.sizing.xs} 12px;
  padding: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.sizing.xxs};
  }
`;

const InfoLabel = styled.div`
  flex-shrink: 0;
  min-width: 120px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: unset;
  }
`;

const InfoValue = styled.div`
  text-align: right;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: left;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xs};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const ConversationContainer = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  // padding-left: ${({ theme }) => theme.sizing.xxs};
  // border-radius: ${({ theme }) => theme.radius.xs};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.sizing.xs};
  // padding: ${({ theme }) => theme.sizing.sm};
  padding: 12px;
  // background-color: ${({ theme }) => theme.fill.defaultMain};
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.sizing.xxs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.sizing.xxs};
  }
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
`;
